import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Sidebar from "./Sidebar";
import Header from "./Header";
import Footer from "./Footer";
import upload from "../image/upload.png";
import scan from "../image/scan.png";
import scanicon from "../image/scanicon.png";
import { ensureValidToken, isOfflineMode } from "../utils/authService";
import "../styles/AddNewCard.css";

// Improved OpenCV loading with singleton pattern
let openCvPromise = null;
let openCvLoaded = false;

const loadOpenCV = () => {
  // If already loaded, return resolved promise
  if (openCvLoaded && window.cv) {
    return Promise.resolve();
  }
  
  // If loading in progress, return existing promise
  if (openCvPromise) {
    return openCvPromise;
  }
  
  // Create new loading promise
  openCvPromise = new Promise((resolve, reject) => {
    // Check if already loaded by another instance
    if (window.cv && window.cv.Mat) {
      openCvLoaded = true;
      resolve();
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://docs.opencv.org/4.8.0/opencv.js';
    script.async = true;
    script.onload = () => {
      // Wait for OpenCV to be fully initialized
      const checkOpenCV = () => {
        if (window.cv && window.cv.Mat) {
          openCvLoaded = true;
          resolve();
        } else {
          setTimeout(checkOpenCV, 50);
        }
      };
      checkOpenCV();
    };
    script.onerror = () => {
      openCvPromise = null;
      reject(new Error("Failed to load OpenCV.js"));
    };
    document.head.appendChild(script);
  });
  
  return openCvPromise;
};

const AddNewCard = ({ editMode = false }) => {
  const [file, setFile] = useState(null);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewType, setPreviewType] = useState("scan");
  const [cameraStream, setCameraStream] = useState(null);
  const [openCvStatus, setOpenCvStatus] = useState('loading');
  const { id } = useParams();
  const [existingFileId, setExistingFileId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const scanCanvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const cropCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const detectionFrameRef = useRef(null);
  const [isDetecting, setIsDetecting] = useState(false);

  const [cropData, setCropData] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    isDragging: false,
    dragType: null,
    originalCrop: null,
    dragStartX: 0,
    dragStartY: 0
  });

  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");

  // Load OpenCV on component mount
  useEffect(() => {
    const initOpenCV = async () => {
      try {
        await loadOpenCV();
        setOpenCvStatus('ready');
        console.log('OpenCV loaded successfully');
      } catch (error) {
        console.error('Failed to load OpenCV:', error);
        setOpenCvStatus('error');
      }
    };
    
    initOpenCV();
    
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
      if (detectionFrameRef.current) {
        cancelAnimationFrame(detectionFrameRef.current);
      }
    };
  }, [cameraStream]);

  const showNotificationMessage = (message) => {
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 3000);
  };

  // Load preview image to canvas when preview is shown
  useEffect(() => {
    if (showPreview && previewImage && previewCanvasRef.current) {
      const canvas = previewCanvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        const containerWidth = canvas.parentElement.offsetWidth;
        const containerHeight = canvas.parentElement.offsetHeight;
        
        const scale = Math.min(
          containerWidth / img.width,
          containerHeight / img.height,
          1
        );
        
        const width = img.width * scale;
        const height = img.height * scale;
        
        canvas.width = img.width;
        canvas.height = img.height;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        if (previewType === "scan") {
          const cropWidth = canvas.width * 0.8;
          const cropHeight = canvas.height * 0.8;
          const cropX = (canvas.width - cropWidth) / 2;
          const cropY = (canvas.height - cropHeight) / 2;
          
          setCropData({
            x: cropX,
            y: cropY,
            width: cropWidth,
            height: cropHeight,
            isDragging: false,
            dragType: null,
            originalCrop: { x: cropX, y: cropY, width: cropWidth, height: cropHeight },
            dragStartX: 0,
            dragStartY: 0
          });
        } else {
          setCropData({
            x: 0,
            y: 0,
            width: canvas.width,
            height: canvas.height,
            isDragging: false,
            dragType: null,
            originalCrop: { x: 0, y: 0, width: canvas.width, height: canvas.height },
            dragStartX: 0,
            dragStartY: 0
          });
        }
      };
      
      img.onerror = () => {
        console.error("Failed to load preview image");
        setShowPreview(false);
        setPreviewImage(null);
      };
      
      img.src = previewImage;
    }
  }, [showPreview, previewImage, previewType]);

  // Continuous detection while scanning
  useEffect(() => {
    if (isScanning && videoRef.current && openCvStatus === 'ready') {
      const detectContinuously = () => {
        if (videoRef.current && videoRef.current.readyState === 4) {
          detectDocumentInFrame();
        }
        detectionFrameRef.current = requestAnimationFrame(detectContinuously);
      };
      
      detectContinuously();
      
      return () => {
        if (detectionFrameRef.current) {
          cancelAnimationFrame(detectionFrameRef.current);
        }
      };
    }
     // eslint-disable-next-line
  }, [isScanning, openCvStatus]);

  const detectDocumentInFrame = async () => {
    if (!videoRef.current || !scanCanvasRef.current || isDetecting || openCvStatus !== 'ready') return;
    
    setIsDetecting(true);
    
    try {
      const video = videoRef.current;
      const canvas = scanCanvasRef.current;
      const ctx = canvas.getContext('2d');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.7);
      const corners = await detectDocumentCorners(imageDataUrl);
      
      drawDetectionOverlay(corners);
      
    } catch (error) {
      // Silently fail for detection frames
      console.debug("Detection frame error:", error);
    } finally {
      setIsDetecting(false);
    }
  };

  const detectDocumentCorners = async (imageSrc) => {
    return new Promise(async (resolve) => {
      try {
        const img = new Image();
        img.onload = async () => {
          try {
            if (openCvStatus !== 'ready' || !window.cv) {
              resolve(null);
              return;
            }
            
            const cv = window.cv;
            const tempCanvas = document.createElement('canvas');
            const tempCtx = tempCanvas.getContext('2d');
            
            const maxDimension = 720;
            let width = img.width;
            let height = img.height;
            const scaleX = img.width / maxDimension;
            const scaleY = img.height / maxDimension;
            const maxScale = Math.max(scaleX, scaleY);
            
            if (maxScale > 1) {
              width = Math.round(img.width / maxScale);
              height = Math.round(img.height / maxScale);
            }
            
            tempCanvas.width = width;
            tempCanvas.height = height;
            tempCtx.drawImage(img, 0, 0, width, height);
            
            const imageData = tempCtx.getImageData(0, 0, width, height);
            const src = cv.matFromImageData(imageData);
            
            // Convert to grayscale
            const gray = new cv.Mat();
            cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
            
            // Apply morphological closing to fill gaps
            const kernel1 = cv.getStructuringElement(cv.MORPH_ELLIPSE, new cv.Size(5, 5));
            const closed = new cv.Mat();
            cv.morphologyEx(gray, closed, cv.MORPH_CLOSE, kernel1);
            
            // Apply threshold to get binary image
            const binary = new cv.Mat();
            cv.threshold(closed, binary, 0, 255, cv.THRESH_BINARY_INV + cv.THRESH_OTSU);
            
            // Apply Gaussian blur for smoothing
            const blurred = new cv.Mat();
            cv.GaussianBlur(binary, blurred, new cv.Size(7, 7), 2);
            
            // Use Canny with adjusted parameters for better detection
            const edges = new cv.Mat();
            cv.Canny(blurred, edges, 30, 100);
            
            // Dilate to connect broken edges
            const kernel2 = cv.Mat.ones(5, 5, cv.CV_8U);
            const dilated = new cv.Mat();
            cv.dilate(edges, dilated, kernel2, new cv.Point(-1, -1), 2);
            
            // Find contours
            const contours = new cv.MatVector();
            const hierarchy = new cv.Mat();
            cv.findContours(dilated, contours, hierarchy, cv.RETR_LIST, cv.CHAIN_APPROX_SIMPLE);
            
            let bestContour = null;
            let maxArea = 0;
            const imageArea = width * height;
            const minArea = imageArea * 0.1;
            const maxAreaThresh = imageArea * 0.98;
            
            for (let i = 0; i < contours.size(); i++) {
              const contour = contours.get(i);
              const area = cv.contourArea(contour);
              const arcLength = cv.arcLength(contour, true);
              
              // Check if contour has minimum length
              if (arcLength < 100) continue;
              
              if (area > minArea && area < maxAreaThresh) {
                const epsilon = 0.02 * arcLength;
                const approx = new cv.Mat();
                cv.approxPolyDP(contour, approx, epsilon, true);
                
                // Look for quadrilaterals (4 vertices)
                if (approx.rows === 4) {
                  // Check if it's roughly a rectangle (aspect ratio check)
                  const points = [];
                  for (let j = 0; j < 4; j++) {
                    points.push({
                      x: approx.data32S[j * 2],
                      y: approx.data32S[j * 2 + 1]
                    });
                  }
                  
                  // Calculate aspect ratio
                  const minX = Math.min(...points.map(p => p.x));
                  const maxX = Math.max(...points.map(p => p.x));
                  const minY = Math.min(...points.map(p => p.y));
                  const maxY = Math.max(...points.map(p => p.y));
                  
                  const w = maxX - minX;
                  const h = maxY - minY;
                  const aspectRatio = w / h;
                  
                  // Business cards are typically 1.4:1 to 2:1 ratio
                  if (aspectRatio > 0.6 && aspectRatio < 3 && area > maxArea) {
                    maxArea = area;
                    if (bestContour) bestContour.delete();
                    bestContour = approx.clone();
                  } else {
                    approx.delete();
                  }
                } else {
                  approx.delete();
                }
              }
            }
            
            let corners = null;
            
            if (bestContour && maxArea > 0) {
              const points = [];
              for (let i = 0; i < 4; i++) {
                points.push({
                  x: bestContour.data32S[i * 2] * (img.width / width),
                  y: bestContour.data32S[i * 2 + 1] * (img.height / height)
                });
              }
              
              corners = sortPointsClockwise(points);
              bestContour.delete();
            }
            
            // Clean up
            src.delete();
            gray.delete();
            closed.delete();
            binary.delete();
            blurred.delete();
            edges.delete();
            dilated.delete();
            kernel1.delete();
            kernel2.delete();
            contours.delete();
            hierarchy.delete();
            
            resolve(corners);
            
          } catch (error) {
            console.error("Detection error:", error);
            resolve(null);
          }
        };
        
        img.onerror = () => resolve(null);
        img.src = imageSrc;
        
      } catch (error) {
        resolve(null);
      }
    });
  };

  const sortPointsClockwise = (points) => {
    const center = points.reduce((acc, p) => ({ x: acc.x + p.x / 4, y: acc.y + p.y / 4 }), { x: 0, y: 0 });
    
    return points.sort((a, b) => {
      const angleA = Math.atan2(a.y - center.y, a.x - center.x);
      const angleB = Math.atan2(b.y - center.y, b.x - center.x);
      return angleA - angleB;
    });
  };

  const drawDetectionOverlay = (corners) => {
    if (!videoRef.current || !scanCanvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = scanCanvasRef.current;
    const ctx = canvas.getContext('2d');
    console.log(video);
    
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    if (corners && corners.length === 4) {
      ctx.strokeStyle = '#4CAF50';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(corners[0].x, corners[0].y);
      for (let i = 1; i < 4; i++) {
        ctx.lineTo(corners[i].x, corners[i].y);
      }
      ctx.closePath();
      ctx.stroke();
      
      corners.forEach(corner => {
        ctx.fillStyle = '#4CAF50';
        ctx.beginPath();
        ctx.arc(corner.x, corner.y, 5, 0, 2 * Math.PI);
        ctx.fill();
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.arc(corner.x, corner.y, 3, 0, 2 * Math.PI);
        ctx.fill();
      });
      
      ctx.fillStyle = '#4CAF50';
      ctx.font = 'bold 16px Arial';
      ctx.fillText('✓ Document Detected', 10, 30);
    } else {
      const width = canvas.width;
      const height = canvas.height;
      const guideWidth = width * 0.8;
      const guideHeight = height * 0.6;
      const guideX = (width - guideWidth) / 2;
      const guideY = (height - guideHeight) / 2;
      
      ctx.strokeStyle = '#FFC107';
      ctx.lineWidth = 2;
      ctx.setLineDash([10, 10]);
      ctx.strokeRect(guideX, guideY, guideWidth, guideHeight);
      ctx.setLineDash([]);
      
      ctx.fillStyle = '#FFC107';
      ctx.font = 'bold 16px Arial';
      ctx.fillText('Align document within frame', 10, 30);
    }
  };

  const startCamera = async () => {
    try {
      setIsScanning(true);
      setError(null);
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        } 
      });
      
      setCameraStream(stream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      
      await new Promise(resolve => {
        if (videoRef.current) {
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play();
            resolve();
          };
        }
      });
      
    } catch (error) {
      console.error("Error accessing camera:", error);
      setError("Unable to access camera. Please check permissions.");
      setIsScanning(false);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsScanning(false);
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    if (scanCanvasRef.current) {
      const ctx = scanCanvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, scanCanvasRef.current.width, scanCanvasRef.current.height);
    }
  };

  const detectAndCropDocument = async (imageSrc) => {
    return new Promise(async (resolve, reject) => {
      try {
        const img = new Image();
        img.onload = async () => {
          try {
            if (openCvStatus !== 'ready' || !window.cv) {
              console.warn("OpenCV not ready, using simple crop");
              const croppedBlob = await simpleCenterCrop(img);
              resolve(croppedBlob);
              return;
            }
            
            const cv = window.cv;
            const tempCanvas = document.createElement('canvas');
            const tempCtx = tempCanvas.getContext('2d');
            tempCanvas.width = img.width;
            tempCanvas.height = img.height;
            tempCtx.drawImage(img, 0, 0);
            
            const imageData = tempCtx.getImageData(0, 0, img.width, img.height);
            const src = cv.matFromImageData(imageData);
            
            const processedBlob = await enhancedProcessWithOpenCV(cv, src, img);
            
            src.delete();
            resolve(processedBlob);
          } catch (cvError) {
            console.error("OpenCV error:", cvError);
            const croppedBlob = await simpleCenterCrop(img);
            resolve(croppedBlob);
          }
        };
        
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = imageSrc;
      } catch (error) {
        reject(error);
      }
    });
  };

  const simpleCenterCrop = (img) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      const aspectRatio = 1.6;
      let cropWidth, cropHeight;
      
      if (img.width / img.height > aspectRatio) {
        cropHeight = img.height * 0.9;
        cropWidth = cropHeight * aspectRatio;
      } else {
        cropWidth = img.width * 0.9;
        cropHeight = cropWidth / aspectRatio;
      }
      
      const cropX = (img.width - cropWidth) / 2;
      const cropY = (img.height - cropHeight) / 2;
      
      canvas.width = cropWidth;
      canvas.height = cropHeight;
      
      ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
      
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.95);
    });
  };

  const enhancedProcessWithOpenCV = (cv, src, originalImg) => {
    return new Promise((resolve) => {
      try {
        const gray = new cv.Mat();
        cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
        
        // Apply morphological closing
        const kernel1 = cv.getStructuringElement(cv.MORPH_ELLIPSE, new cv.Size(5, 5));
        const closed = new cv.Mat();
        cv.morphologyEx(gray, closed, cv.MORPH_CLOSE, kernel1);
        
        // Apply threshold
        const binary = new cv.Mat();
        cv.threshold(closed, binary, 0, 255, cv.THRESH_BINARY_INV + cv.THRESH_OTSU);
        
        // Apply Gaussian blur
        const blurred = new cv.Mat();
        cv.GaussianBlur(binary, blurred, new cv.Size(7, 7), 2);
        
        // Canny edge detection with better parameters
        const edges = new cv.Mat();
        cv.Canny(blurred, edges, 30, 100);
        
        // Dilate to connect edges
        const kernel2 = cv.Mat.ones(5, 5, cv.CV_8U);
        const dilated = new cv.Mat();
        cv.dilate(edges, dilated, kernel2, new cv.Point(-1, -1), 2);
        
        // Find contours
        const contours = new cv.MatVector();
        const hierarchy = new cv.Mat();
        cv.findContours(dilated, contours, hierarchy, cv.RETR_LIST, cv.CHAIN_APPROX_SIMPLE);
        
        let bestContour = null;
        let maxArea = 0;
        const imageArea = originalImg.width * originalImg.height;
        const minArea = imageArea * 0.1;
        const maxAreaThresh = imageArea * 0.98;
        
        for (let i = 0; i < contours.size(); i++) {
          const contour = contours.get(i);
          const area = cv.contourArea(contour);
          const arcLength = cv.arcLength(contour, true);
          
          if (arcLength < 100) continue;
          
          if (area > minArea && area < maxAreaThresh) {
            const epsilon = 0.02 * arcLength;
            const approx = new cv.Mat();
            cv.approxPolyDP(contour, approx, epsilon, true);
            
            if (approx.rows === 4) {
              const points = [];
              for (let j = 0; j < 4; j++) {
                points.push({
                  x: approx.data32S[j * 2],
                  y: approx.data32S[j * 2 + 1]
                });
              }
              
              const minX = Math.min(...points.map(p => p.x));
              const maxX = Math.max(...points.map(p => p.x));
              const minY = Math.min(...points.map(p => p.y));
              const maxY = Math.max(...points.map(p => p.y));
              
              const w = maxX - minX;
              const h = maxY - minY;
              const aspectRatio = w / h;
              
              if (aspectRatio > 0.6 && aspectRatio < 3 && area > maxArea) {
                maxArea = area;
                if (bestContour) bestContour.delete();
                bestContour = approx.clone();
              } else {
                approx.delete();
              }
            } else {
              approx.delete();
            }
          }
        }
        
        if (bestContour && maxArea > 0) {
          const points = [];
          for (let i = 0; i < 4; i++) {
            points.push({
              x: bestContour.data32S[i * 2],
              y: bestContour.data32S[i * 2 + 1]
            });
          }
          
          const sortedPoints = sortPointsClockwise(points);
          
          // Use detected points for perspective transform
          applyPerspectiveTransform(cv, src, sortedPoints).then(blob => {
            gray.delete();
            closed.delete();
            binary.delete();
            blurred.delete();
            edges.delete();
            dilated.delete();
            kernel1.delete();
            kernel2.delete();
            contours.delete();
            hierarchy.delete();
            if (bestContour) bestContour.delete();
            resolve(blob);
          }).catch(() => {
            gray.delete();
            closed.delete();
            binary.delete();
            blurred.delete();
            edges.delete();
            dilated.delete();
            kernel1.delete();
            kernel2.delete();
            contours.delete();
            hierarchy.delete();
            if (bestContour) bestContour.delete();
            simpleCenterCrop(originalImg).then(resolve);
          });
        } else {
          gray.delete();
          closed.delete();
          binary.delete();
          blurred.delete();
          edges.delete();
          dilated.delete();
          kernel1.delete();
          kernel2.delete();
          contours.delete();
          hierarchy.delete();
          if (bestContour) bestContour.delete();
          simpleCenterCrop(originalImg).then(resolve);
        }
      } catch (error) {
        console.error("Enhanced processing error:", error);
        simpleCenterCrop(originalImg).then(resolve);
      }
    });
  };

  const applyPerspectiveTransform = (cv, src, points) => {
    return new Promise((resolve) => {
      try {
        // Standard business card dimensions (600x375 @ 100 DPI)
        const cardWidth = 650;
        const cardHeight = 410;
        
        // Validate and reorder points if needed
        const srcPoints = cv.matFromArray(4, 1, cv.CV_32FC2, [
          points[0].x, points[0].y,
          points[1].x, points[1].y,
          points[2].x, points[2].y,
          points[3].x, points[3].y
        ]);
        
        const dstPoints = cv.matFromArray(4, 1, cv.CV_32FC2, [
          0, 0,
          cardWidth, 0,
          cardWidth, cardHeight,
          0, cardHeight
        ]);
        
        // Get perspective transformation matrix
        const M = cv.getPerspectiveTransform(srcPoints, dstPoints);
        const warped = new cv.Mat();
        
        // Apply the perspective transformation
        cv.warpPerspective(src, warped, M, new cv.Size(cardWidth, cardHeight), cv.INTER_LINEAR);
        
        // Apply brightness/contrast enhancement
        const enhanced = new cv.Mat();
        warped.convertTo(enhanced, -1, 1.2, 10);
        
        // Convert to canvas and blob
        const resultCanvas = document.createElement('canvas');
        resultCanvas.width = cardWidth;
        resultCanvas.height = cardHeight;
        cv.imshow(resultCanvas, enhanced);
        
        resultCanvas.toBlob((blob) => {
          srcPoints.delete();
          dstPoints.delete();
          M.delete();
          warped.delete();
          enhanced.delete();
          resolve(blob);
        }, 'image/jpeg', 0.95);
      } catch (error) {
        console.error("Perspective transform error:", error);
        resolve(null);
      }
    });
  };

  const captureImage = async () => {
    if (!videoRef.current || !scanCanvasRef.current) return;
    
    try {
      setIsProcessing(true);
      setError(null);
      
      const video = videoRef.current;
      const canvas = scanCanvasRef.current;
      const ctx = canvas.getContext('2d');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.95);
      const processedBlob = await detectAndCropDocument(imageDataUrl);
      
      const previewUrl = URL.createObjectURL(processedBlob);
      setPreviewImage(previewUrl);
      setPreviewType("scan");
      setShowPreview(true);
      stopCamera();
      
    } catch (error) {
      console.error("Capture error:", error);
      setError("Failed to process image. Please try again.");
      stopCamera();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCropStart = (e) => {
    const canvas = previewCanvasRef.current;
    if (!canvas || previewType === "upload") return;
    
    e.preventDefault();
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    
    if (!clientX || !clientY) return;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    const { x: cropX, y: cropY, width: cropWidth, height: cropHeight } = cropData;
    
    // Increased hit zone for better touch/click detection
    const handleSize = 25;
    const edgeSize = 30;
    
    // Check corners first
    const isTopLeft = x >= cropX - handleSize && x <= cropX + handleSize && 
                      y >= cropY - handleSize && y <= cropY + handleSize;
    const isTopRight = x >= cropX + cropWidth - handleSize && x <= cropX + cropWidth + handleSize && 
                       y >= cropY - handleSize && y <= cropY + handleSize;
    const isBottomLeft = x >= cropX - handleSize && x <= cropX + handleSize && 
                         y >= cropY + cropHeight - handleSize && y <= cropY + cropHeight + handleSize;
    const isBottomRight = x >= cropX + cropWidth - handleSize && x <= cropX + cropWidth + handleSize && 
                          y >= cropY + cropHeight - handleSize && y <= cropY + cropHeight + handleSize;
    
    // Check edges
    const isTopEdge = x > cropX + handleSize && x < cropX + cropWidth - handleSize && 
                      y >= cropY - edgeSize && y <= cropY + edgeSize;
    const isBottomEdge = x > cropX + handleSize && x < cropX + cropWidth - handleSize && 
                         y >= cropY + cropHeight - edgeSize && y <= cropY + cropHeight + edgeSize;
    const isLeftEdge = y > cropY + handleSize && y < cropY + cropHeight - handleSize && 
                       x >= cropX - edgeSize && x <= cropX + edgeSize;
    const isRightEdge = y > cropY + handleSize && y < cropY + cropHeight - handleSize && 
                        x >= cropX + cropWidth - edgeSize && x <= cropX + cropWidth + edgeSize;
    
    let dragType = null;
    if (isTopLeft) dragType = 'top-left';
    else if (isTopRight) dragType = 'top-right';
    else if (isBottomLeft) dragType = 'bottom-left';
    else if (isBottomRight) dragType = 'bottom-right';
    else if (isTopEdge) dragType = 'top';
    else if (isBottomEdge) dragType = 'bottom';
    else if (isLeftEdge) dragType = 'left';
    else if (isRightEdge) dragType = 'right';
    else if (x >= cropX && x <= cropX + cropWidth && y >= cropY && y <= cropY + cropHeight) {
      dragType = 'move';
    }
    
    if (dragType) {
      setCropData(prev => ({
        ...prev,
        isDragging: true,
        dragType,
        originalCrop: { x: cropX, y: cropY, width: cropWidth, height: cropHeight },
        dragStartX: x,
        dragStartY: y
      }));
    }
  };

  const handleCropMove = (e) => {
    if (!cropData.isDragging || !previewCanvasRef.current || previewType === "upload") return;
    
    e.preventDefault();
    
    const canvas = previewCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    
    if (!clientX || !clientY) return;
    
    const currentX = (clientX - rect.left) * scaleX;
    const currentY = (clientY - rect.top) * scaleY;
    
    const { dragType, originalCrop, dragStartX, dragStartY } = cropData;
    const deltaX = currentX - dragStartX;
    const deltaY = currentY - dragStartY;
    
    let newCrop = { ...originalCrop };
    const minSize = 80;
    const maxWidth = canvas.width;
    const maxHeight = canvas.height;
    
    switch (dragType) {
      case 'move':
        newCrop.x = Math.max(0, Math.min(maxWidth - newCrop.width, originalCrop.x + deltaX));
        newCrop.y = Math.max(0, Math.min(maxHeight - newCrop.height, originalCrop.y + deltaY));
        break;
        
      case 'top-left':
        newCrop.x = Math.max(0, Math.min(originalCrop.x + originalCrop.width - minSize, originalCrop.x + deltaX));
        newCrop.y = Math.max(0, Math.min(originalCrop.y + originalCrop.height - minSize, originalCrop.y + deltaY));
        newCrop.width = Math.max(minSize, originalCrop.width - (newCrop.x - originalCrop.x));
        newCrop.height = Math.max(minSize, originalCrop.height - (newCrop.y - originalCrop.y));
        break;
        
      case 'top-right':
        newCrop.y = Math.max(0, Math.min(originalCrop.y + originalCrop.height - minSize, originalCrop.y + deltaY));
        newCrop.width = Math.max(minSize, Math.min(maxWidth - originalCrop.x, originalCrop.width + deltaX));
        newCrop.height = Math.max(minSize, originalCrop.height - (newCrop.y - originalCrop.y));
        break;
        
      case 'bottom-left':
        newCrop.x = Math.max(0, Math.min(originalCrop.x + originalCrop.width - minSize, originalCrop.x + deltaX));
        newCrop.width = Math.max(minSize, originalCrop.width - (newCrop.x - originalCrop.x));
        newCrop.height = Math.max(minSize, Math.min(maxHeight - originalCrop.y, originalCrop.height + deltaY));
        break;
        
      case 'bottom-right':
        newCrop.width = Math.max(minSize, Math.min(maxWidth - originalCrop.x, originalCrop.width + deltaX));
        newCrop.height = Math.max(minSize, Math.min(maxHeight - originalCrop.y, originalCrop.height + deltaY));
        break;
        
      case 'top':
        newCrop.y = Math.max(0, Math.min(originalCrop.y + originalCrop.height - minSize, originalCrop.y + deltaY));
        newCrop.height = Math.max(minSize, originalCrop.height - (newCrop.y - originalCrop.y));
        break;
        
      case 'bottom':
        newCrop.height = Math.max(minSize, Math.min(maxHeight - originalCrop.y, originalCrop.height + deltaY));
        break;
        
      case 'left':
        newCrop.x = Math.max(0, Math.min(originalCrop.x + originalCrop.width - minSize, originalCrop.x + deltaX));
        newCrop.width = Math.max(minSize, originalCrop.width - (newCrop.x - originalCrop.x));
        break;
        
      case 'right':
        newCrop.width = Math.max(minSize, Math.min(maxWidth - originalCrop.x, originalCrop.width + deltaX));
        break;
        
      default:
        break;
    }
    
    setCropData(prev => ({
      ...prev,
      x: newCrop.x,
      y: newCrop.y,
      width: newCrop.width,
      height: newCrop.height
    }));
  };

  const handleCropEnd = () => {
    setCropData(prev => ({ ...prev, isDragging: false, dragType: null }));
  };

  const handlePreviewConfirm = () => {
    if (previewImage && previewCanvasRef.current) {
      const canvas = previewCanvasRef.current;
      const ctx = canvas.getContext('2d');
      
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      
      if (previewType === "scan") {
        const { x, y, width, height } = cropData;
        
        tempCanvas.width = width;
        tempCanvas.height = height;
        
        const imageData = ctx.getImageData(x, y, width, height);
        tempCtx.putImageData(imageData, 0, 0);
        
        tempCanvas.toBlob((blob) => {
          const processedFile = new File([blob], `scanned_${Date.now()}.jpg`, {
            type: 'image/jpeg'
          });
          
          setFile(processedFile);
          showNotificationMessage("Document scanned and cropped successfully!");
          setShowPreview(false);
          setPreviewImage(null);
          setCropData({
            x: 0, y: 0, width: 0, height: 0,
            isDragging: false, dragType: null, originalCrop: null,
            dragStartX: 0, dragStartY: 0
          });
        }, 'image/jpeg', 0.95);
      } else {
        // For upload, if no crop was done, keep the original file
        if (cropData.width === canvas.width && cropData.height === canvas.height && 
            cropData.x === 0 && cropData.y === 0) {
          // No cropping done, keep original file
          showNotificationMessage("File uploaded successfully!");
          setShowPreview(false);
          setPreviewImage(null);
          setCropData({
            x: 0, y: 0, width: 0, height: 0,
            isDragging: false, dragType: null, originalCrop: null,
            dragStartX: 0, dragStartY: 0
          });
        } else {
          // Crop was done, create cropped file
          const { x, y, width, height } = cropData;
          
          tempCanvas.width = width;
          tempCanvas.height = height;
          
          const imageData = ctx.getImageData(x, y, width, height);
          tempCtx.putImageData(imageData, 0, 0);
          
          tempCanvas.toBlob((blob) => {
            const processedFile = new File([blob], `uploaded_${Date.now()}.jpg`, {
              type: 'image/jpeg'
            });
            
            setFile(processedFile);
            showNotificationMessage("File uploaded and cropped successfully!");
            setShowPreview(false);
            setPreviewImage(null);
            setCropData({
              x: 0, y: 0, width: 0, height: 0,
              isDragging: false, dragType: null, originalCrop: null,
              dragStartX: 0, dragStartY: 0
            });
          }, 'image/jpeg', 0.95);
        }
      }
    }
  };

  const handlePreviewCancel = () => {
    if (previewImage) {
      URL.revokeObjectURL(previewImage);
    }
    setShowPreview(false);
    setPreviewImage(null);
    setCropData({
      x: 0, y: 0, width: 0, height: 0,
      isDragging: false, dragType: null, originalCrop: null,
      dragStartX: 0, dragStartY: 0
    });
    
    if (previewType === "scan") {
      startCamera();
    } else {
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      try {
        setIsProcessing(true);
        setError(null);
        
        // Set the file immediately so it's available
        setFile(selectedFile);
        
        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const previewUrl = URL.createObjectURL(selectedFile);
            setPreviewImage(previewUrl);
            setPreviewType("upload");
            setShowPreview(true);
          } catch (error) {
            console.error("Preview creation error:", error);
            // File is already set, so we can just show success notification
            showNotificationMessage("✓ File uploaded successfully!");
          } finally {
            setIsProcessing(false);
          }
        };
        
        reader.onerror = () => {
          setIsProcessing(false);
          // File is already set, show success notification
          showNotificationMessage("✓ File uploaded successfully!");
        };
        
        reader.readAsDataURL(selectedFile);
        
      } catch (error) {
        console.error("Error processing file:", error);
        setIsProcessing(false);
        setError("Failed to process file. Please try again.");
      }
    }
  };

  const handleScanClick = async () => {
    if (openCvStatus === 'error') {
      setError("OpenCV failed to load. Using basic scanning mode.");
    }
    
    if (isScanning) {
      await captureImage();
    } else {
      await startCamera();
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  useEffect(() => {
    if (editMode && id) {
      const fetchCardData = async () => {
        if (isOfflineMode()) return;
        try {
          const accessToken = await ensureValidToken();
          if (!accessToken) {
            navigate("/login");
            return;
          }
          const response = await axios.get(
            `https://api.nexcard.co.in/api/cards/${id}/`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
          if (response.data.file?.id) {
            setExistingFileId(response.data.file.id);
          }
        } catch (error) {
          console.error("Error fetching card data:", error);
        }
      };
      fetchCardData();
    }
  }, [editMode, id, navigate]);

  const handleSaveAndContinue = async () => {
    if (!file) {
      setError("Please upload or scan a file.");
      return;
    }

    if (isOfflineMode()) {
      setError("You are offline. Uploading files is disabled in offline mode.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const accessToken = await ensureValidToken();
      if (!accessToken) {
        setError("Access token not found. Please log in again.");
        navigate("/login");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      let fileId;
      
      if (editMode && existingFileId) {
        const response = await axios.patch(
          `https://api.nexcard.co.in/api/cards/file/${existingFileId}/`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        fileId = existingFileId;
        console.log(response);
        
      } else {
        const response = await axios.post(
          "https://api.nexcard.co.in/api/cards/file/",
          formData,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        fileId = response.data.id;
      }

      sessionStorage.setItem("uploadedFileId", fileId);

      navigate(editMode ? `/edit-card/${id}/step2` : "/add-new-card2", {
        state: {
          ...location.state,
          fromSharedCard: location.state?.from?.includes('/shared-card/'),
          sharedCardId: location.state?.from?.match(/shared-card\/([^/]+)/)?.[1]
        }
      });

    } catch (error) {
      console.error("File upload failed:", error);
      setError(error.response?.data?.message || "File upload failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (isScanning) stopCamera();
    if (showPreview) {
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
      setShowPreview(false);
      setPreviewImage(null);
    }
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    navigate(-1);
  };

  const handleClick = (message) => {
    alert(message);
  };

  useEffect(() => {
    return () => {
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  return (
    <>
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <Header toggleSidebar={toggleSidebar} />

      <div className="main-content">
        <div className="add-card-container2">
          <h2 className="add-card-title2">{editMode ? "Edit Card" : "Add New Card"}</h2>

          <div className="steps-container2">
            <div className="step2 active-step2">
              <span className="step-number2">1</span>
              <div className="step-label2">
                Upload/Scan <br /> Business card
              </div>
            </div>

            <div className="line"></div>

            <div className="step2">
              <span className="step-number2">2</span>
              <div className="step-label2">
                Submit <br /> Profile Details
              </div>
            </div>
          </div>

          {/* OpenCV Status Indicator */}
          {openCvStatus === 'loading' && (
            <div className="opencv-status loading">
              Loading advanced scanning features...
            </div>
          )}
          {openCvStatus === 'error' && (
            <div className="opencv-status error">
              Using basic scanning mode
            </div>
          )}

          {/* Notification Toast */}
          {showNotification && (
            <div className="notification-toast">
              <div className="notification-content">
                <span className="notification-check">✓</span>
                <span className="notification-message">{notificationMessage}</span>
              </div>
            </div>
          )}

          {isProcessing && (
            <div className="processing-overlay">
              <div className="processing-modal">
                <div className="processing-spinner"></div>
                <p className="processing-text">Processing image...</p>
                <p className="processing-subtext">Detecting and cropping document</p>
              </div>
            </div>
          )}

          {isScanning && (
            <div className="camera-modal-overlay">
              <div className="camera-modal">
                <div className="camera-header">
                  <h3>Scan Business Card</h3>
                  <button className="close-camera-btn" onClick={stopCamera}>
                    ✕
                  </button>
                </div>
                <div className="camera-preview">
                  <video ref={videoRef} autoPlay playsInline className="camera-video" />
                  <canvas 
                    ref={scanCanvasRef} 
                    className="camera-overlay-canvas" 
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                  />
                </div>
                <div className="camera-controls">
                  <button className="capture-btn" onClick={captureImage} disabled={isProcessing}>
                    <div className="capture-circle"></div>
                  </button>
                </div>
                <p className="camera-instruction">
                  Align the business card within the frame. Green outline indicates detection.
                </p>
              </div>
            </div>
          )}

          {showPreview && (
            <div className="preview-overlay">
              <div className="preview-modal">
                <div className="preview-header">
                  <h3>{previewType === "scan" ? "Review & Crop" : "Preview & Crop (Optional)"}</h3>
                  <button className="close-preview-btn" onClick={handlePreviewCancel}>
                    ✕
                  </button>
                </div>
                <div className="preview-content">
                  <div className="preview-instruction">
                    {previewType === "scan" 
                      ? "Drag corners or edges to adjust crop area" 
                      : "You can crop the image or click OK to use the full image"}
                  </div>
                  <div 
                    className="preview-container"
                    onMouseDown={handleCropStart}
                    onMouseMove={handleCropMove}
                    onMouseUp={handleCropEnd}
                    onMouseLeave={handleCropEnd}
                    onTouchStart={handleCropStart}
                    onTouchMove={handleCropMove}
                    onTouchEnd={handleCropEnd}
                  >
                    <canvas 
                      ref={previewCanvasRef} 
                      className="preview-canvas"
                      style={{ display: 'block' }}
                    />
                    {cropData.width > 0 && cropData.height > 0 && (
                      <div 
                        className="crop-selection"
                        style={{
                          left: `${cropData.x * (previewCanvasRef.current?.parentElement?.offsetWidth / previewCanvasRef.current?.width || 1)}px`,
                          top: `${cropData.y * (previewCanvasRef.current?.parentElement?.offsetHeight / previewCanvasRef.current?.height || 1)}px`,
                          width: `${cropData.width * (previewCanvasRef.current?.parentElement?.offsetWidth / previewCanvasRef.current?.width || 1)}px`,
                          height: `${cropData.height * (previewCanvasRef.current?.parentElement?.offsetHeight / previewCanvasRef.current?.height || 1)}px`
                        }}
                      >
                        <div className="crop-handle top-left"></div>
                        <div className="crop-handle top-right"></div>
                        <div className="crop-handle bottom-left"></div>
                        <div className="crop-handle bottom-right"></div>
                        <div className="crop-edge top"></div>
                        <div className="crop-edge bottom"></div>
                        <div className="crop-edge left"></div>
                        <div className="crop-edge right"></div>
                        <div className="crop-area"></div>
                      </div>
                    )}
                  </div>
                  <div className="preview-controls">
                    <button className="preview-cancel-btn" onClick={handlePreviewCancel}>
                      {previewType === "scan" ? "Rescan" : "Cancel & Upload New"}
                    </button>
                    <button className="preview-ok-btn" onClick={handlePreviewConfirm}>
                      OK
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="upload-options2">
            <div className="scan-option2">
              <div 
                className="scan-option-container"
                onClick={handleScanClick}
                style={{ cursor: "pointer" }}
              >
                <img src={scan} alt="Scan Business Card" className="scan-icon2" />
                <button className="scan-btn2">
                  <img src={scanicon} alt="Scan Icon" className="scan-icon-btn2" />
                </button>
                <p className="z">{isScanning ? "Capturing..." : "Scan Your"}</p>
                <p className="z">{isScanning ? "Click to Capture" : "Business Card"}</p>
              </div>
            </div>

            <div className="or-divider2">OR</div>

            <div className="upload-option2">
              <div className="upload-box2">
                <img src={upload} alt="Upload" className="upload-icon2" />
                  <>
                    <p className="z">Upload a file or drag & drop it here.</p>
                    <p className="y">JPEG, PNG, PDF (max 10MB).</p>
                  </>
                
                <button
                  className="upload-btn2"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isProcessing || isLoading}
                >
                  {isProcessing ? "Processing..." : "Upload File"}
                </button>

                <input
                  ref={fileInputRef}
                  id="file-upload"
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={handleFileChange}
                  hidden
                  disabled={isProcessing || isLoading}
                />

                {file && (
                  <div className="file-info">
                    <p className="file-name2">{file.name}</p>
                    <p className="file-size2">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {error && (
            <p className="error-message">
              {error}
            </p>
          )}

          <div className="button-group2">
            <button 
              className="cancel-btn2" 
              onClick={handleCancel}
              disabled={isLoading || isProcessing}
            >
              Cancel
            </button>
            <button
              className="save-btn2"
              onClick={handleSaveAndContinue}
              disabled={isLoading || isProcessing || !file}
            >
              {isLoading ? "Uploading..." : "Save & Continue"}
            </button>
          </div>
        </div>
      </div>

      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <canvas ref={cropCanvasRef} style={{ display: 'none' }} />

      <Footer handleClick={handleClick} />
    </>
  );
};

export default AddNewCard;